import { Button, Card, ErrorState, Loading, formatRelative } from "@corecrm/ui";
import { useState } from "react";
import { useDisconnectClaude, useMcpAccess } from "../api/queries";

/* Using Claude with the portal (UX-197): the address to add to Claude as a custom connector, how
   to connect, what Claude can and can't do, and the connections to revoke. Only shown when the
   client's CoreNode team turned Claude access on for them. */
export function ClaudePage() {
  const access = useMcpAccess();
  const disconnect = useDisconnectClaude();
  const [copied, setCopied] = useState(false);
  if (access.isPending) return <Loading />;
  if (access.error) return <ErrorState error={access.error} onRetry={() => access.refetch()} />;
  const { enabled, url, connections } = access.data;
  if (!enabled || !url) {
    return (
      <Card title="Use with Claude">
        <p className="muted">
          Claude access to the portal isn&apos;t turned on for you. Ask your CoreNode project manager if
          you&apos;d like to use it.
        </p>
      </Card>
    );
  }
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div className="stack">
      <Card title="Use with Claude">
        <p>
          Connect Claude to your client portal to ask about your projects in plain language: where things
          stand, what&apos;s waiting on you, your quotes and invoices. Claude can also answer the team&apos;s
          questions for you and mark your tasks done.
        </p>
        <div className="fld">
          <span className="lbl" id="mcp-url-label">
            Connector address
          </span>
          <div className="row">
            <code aria-labelledby="mcp-url-label" data-testid="mcp-url">
              {url}
            </code>
            <Button size="sm" onClick={copy}>
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
        </div>
        <ol>
          <li>
            In Claude, open <strong>Settings &gt; Connectors</strong> and choose{" "}
            <strong>Add custom connector</strong>.
          </li>
          <li>Name it &quot;CoreNode client portal&quot; and paste the address above.</li>
          <li>
            Choose <strong>Connect</strong>, enter the email you use for this portal, then the code we email
            you, and allow access.
          </li>
          <li>Ask Claude things like &quot;What&apos;s the status of my project?&quot;</li>
        </ol>
        <p className="faint">
          Claude sees what you see here and acts as you. It can&apos;t accept or decline quotes or change
          requests, approve deliverables or pay invoices: you do those here in the portal.
        </p>
      </Card>
      <Card title="Connected">
        {connections.length ? (
          <>
            <ul className="plain">
              {connections.map((c) => (
                <li key={`${c.client_name}-${c.connected_at}`}>
                  <strong>{c.client_name}</strong>{" "}
                  <span className="faint">
                    connected {formatRelative(c.connected_at)}
                    {c.last_used_at ? `, last used ${formatRelative(c.last_used_at)}` : ""}
                  </span>
                </li>
              ))}
            </ul>
            <Button busy={disconnect.isPending} onClick={() => disconnect.mutate()}>
              Disconnect all
            </Button>
          </>
        ) : (
          <p className="muted">Nothing connected yet.</p>
        )}
        {disconnect.isSuccess && <p role="status">Disconnected. Connect again from Claude any time.</p>}
      </Card>
    </div>
  );
}
